import React, { useEffect, useState, useRef, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import { Link } from 'react-router-dom';
import Logo from '../components/common/Logo';
import { motion, AnimatePresence } from 'framer-motion';
import Masonry from 'react-masonry-css';
import { 
  PlusIcon, 
  FunnelIcon, 
  MagnifyingGlassIcon,
  XMarkIcon,
  BriefcaseIcon,
  SparklesIcon,
  ListBulletIcon
} from '@heroicons/react/24/outline';
import { useAppSelector, useAppDispatch } from '../hooks/redux';
import { fetchOpportunities, setFilters, fetchMyApplications, fetchRecommendations } from '../store/slices/opportunitiesSlice';
import Button from '../components/common/Button';
import OpportunityCard from '../components/opportunities/OpportunityCard';
import SEOHead from '../components/common/SEOHead';
import GuestHeader from '../components/layout/GuestHeader';
import { OPPORTUNITY_TYPES as OPP_CONSTANTS } from '../constants/opportunities';

const FILTER_OPTIONS = {
  types: [
    { value: '', label: 'All Types' },
    { value: OPP_CONSTANTS.PHOTOGRAPHER, label: 'Photographer Needed' },
    { value: OPP_CONSTANTS.VIDEOGRAPHER, label: 'Videographer Needed' },
    { value: OPP_CONSTANTS.BOTH_CREATORS, label: 'Photographer & Videographer Needed' },
    { value: OPP_CONSTANTS.CREATOR, label: 'Creator Needed (Legacy)' },
    { value: OPP_CONSTANTS.MODEL, label: 'Model Needed' },
    { value: OPP_CONSTANTS.BOTH, label: 'Looking for Both (Creator & Model)' },
    { value: OPP_CONSTANTS.CREATIVE_COLLABORATION, label: 'Creative Collaboration' },
  ],
  experience: [
    { value: '', label: 'Any Level' },
    { value: 'beginner', label: 'Beginner' },
    { value: 'intermediate', label: 'Intermediate' },
    { value: 'professional', label: 'Professional' },
  ],
  compensation: [
    { value: '', label: 'All Types' },
    { value: 'paid', label: 'Paid' },
    { value: 'tfp', label: 'TFP' },
    { value: 'negotiable', label: 'Negotiable' },
    { value: 'unpaid', label: 'Unpaid' },
  ],
};

interface FilterSectionProps {
  searchInput: string;
  setSearchInput: (value: string) => void;
  filters: any;
  onFilterChange: (key: string, value: any) => void;
  onSearch: (e: React.FormEvent) => void;
  onClear: () => void;
}

const FilterSection: React.FC<FilterSectionProps> = ({
  searchInput,
  setSearchInput,
  filters,
  onFilterChange,
  onSearch,
  onClear
}) => (
  <div className="space-y-6">
    {/* Search */}
    <div>
      <label className="block text-sm font-bold text-gray-900 dark:text-white mb-2">Search</label>
      <form onSubmit={onSearch} className="relative">
        <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
        <input
          type="text"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder="Keywords..."
          className="w-full pl-10 pr-4 py-2.5 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-black dark:focus:ring-white focus:border-transparent text-sm transition-all outline-none bg-gray-50 dark:bg-gray-800 focus:bg-white dark:focus:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
        />
      </form>
    </div>

    {/* Type */}
    <div>
      <label className="block text-sm font-bold text-gray-900 dark:text-white mb-2">Type</label>
      <div className="space-y-2">
        {FILTER_OPTIONS.types.map((option) => (
          <label key={option.value} className="flex items-center space-x-3 cursor-pointer group p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors -mx-2">
            <div className="relative flex items-center">
              <input
                type="radio"
                name="type"
                checked={filters.type === option.value}
                onChange={() => onFilterChange('type', option.value)}
                className="peer h-4 w-4 border-gray-300 dark:border-gray-600 text-black dark:text-white focus:ring-black dark:focus:ring-white bg-white dark:bg-gray-700"
              />
            </div>
            <span className={`text-sm ${filters.type === option.value ? 'text-black dark:text-white font-medium' : 'text-gray-600 dark:text-gray-400 group-hover:text-gray-900 dark:group-hover:text-white'}`}>
              {option.label}
            </span>
          </label>
        ))}
      </div>
    </div>

    {/* Location */}
    <div>
      <label className="block text-sm font-bold text-gray-900 dark:text-white mb-2">Location</label>
      <input
        type="text"
        value={filters.location || ''}
        onChange={(e) => onFilterChange('location', e.target.value)}
        placeholder="City or region"
        className="w-full px-3 py-2.5 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-black dark:focus:ring-white focus:border-transparent text-sm transition-all outline-none bg-gray-50 dark:bg-gray-800 focus:bg-white dark:focus:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
      />
    </div>

    {/* Compensation */}
    <div>
      <label className="block text-sm font-bold text-gray-900 dark:text-white mb-2">Compensation</label>
      <select
        value={filters.compensation || ''}
        onChange={(e) => onFilterChange('compensation', e.target.value)}
        className="w-full px-3 py-2.5 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-black dark:focus:ring-white focus:border-transparent text-sm transition-all outline-none bg-gray-50 dark:bg-gray-800 focus:bg-white dark:focus:bg-gray-700 text-gray-900 dark:text-white"
      >
        {FILTER_OPTIONS.compensation.map((option) => (
          <option key={option.value} value={option.value} className="bg-white dark:bg-gray-800">
            {option.label}
          </option>
        ))}
      </select>
    </div>

    {/* Experience */}
    <div>
      <label className="block text-sm font-bold text-gray-900 dark:text-white mb-2">Experience Level</label>
      <select
        value={filters.experience || ''}
        onChange={(e) => onFilterChange('experience', e.target.value)}
        className="w-full px-3 py-2.5 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-black dark:focus:ring-white focus:border-transparent text-sm transition-all outline-none bg-gray-50 dark:bg-gray-800 focus:bg-white dark:focus:bg-gray-700 text-gray-900 dark:text-white"
      >
        {FILTER_OPTIONS.experience.map((option) => (
          <option key={option.value} value={option.value} className="bg-white dark:bg-gray-800">
            {option.label}
          </option>
        ))}
      </select>
    </div>

    {/* Clear Filters */}
    <button
      onClick={onClear}
      className="w-full py-2.5 text-sm font-medium text-gray-500 dark:text-gray-400 hover:text-black dark:hover:text-white border border-dashed border-gray-300 dark:border-gray-600 rounded-xl hover:border-black dark:hover:border-white transition-all hover:bg-gray-50 dark:hover:bg-gray-800"
    >
      Clear All Filters
    </button>
  </div>
);

const OpportunitiesPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { opportunities, recommendations, loading, filters, pagination, myApplications } = useAppSelector((state) => state.opportunities);
  const { user, isAuthenticated } = useAppSelector((state) => state.auth);

  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [viewMode, setViewMode] = useState<'all' | 'recommended'>('recommended');

  const [hasFetchedRecommendations, setHasFetchedRecommendations] = useState(false);

  useEffect(() => {
    if (viewMode === 'all') {
      dispatch(fetchOpportunities(filters));
    } else {
      dispatch(fetchRecommendations())
        .unwrap()
        .finally(() => setHasFetchedRecommendations(true));
    }
    
    if (isAuthenticated) {
      dispatch(fetchMyApplications());
    }
  }, [dispatch, filters, isAuthenticated, viewMode]);

  // Fallback to 'all' if recommendations are empty
  useEffect(() => {
    if (viewMode === 'recommended' && hasFetchedRecommendations && recommendations.length === 0) {
      setViewMode('all');
    }
  }, [hasFetchedRecommendations, recommendations, viewMode]);

  // Infinite Scroll Refs
  const sentinelRef = useRef<HTMLDivElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  // Infinite Scroll Observer
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || viewMode === 'recommended') return; // Disable infinite scroll for recommendations for now

    if (observerRef.current) observerRef.current.disconnect();

    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && filters.page < pagination.pages && !loading) {
          loadMore();
        }
      },
      { rootMargin: '800px' }
    );

    observerRef.current.observe(sentinel);
    return () => observerRef.current?.disconnect();
  }, [filters.page, pagination.pages, loading, viewMode]);



  const handleFilterChange = (key: string, value: any) => {
    dispatch(setFilters({ ...filters, [key]: value, page: 1 }));
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    handleFilterChange('search', searchInput);
  };

  const clearFilters = () => {
    dispatch(setFilters({
      page: 1,
      limit: 10,
      search: '',
      type: '',
      location: '',
      experience: '',
      compensation: ''
    }));
    setSearchInput('');
  };

  const loadMore = () => {
    if (filters.page < pagination.pages) {
      dispatch(setFilters({ ...filters, page: filters.page + 1 }));
    }
  };

  const currentList = viewMode === 'recommended' ? recommendations : opportunities;
  
  // Filter out applied opportunities
  const unappliedOpportunities = (currentList || []).filter(
    opp => !Array.isArray(myApplications) || !myApplications.some(app => app.opportunity_id === opp.id)
  );

  // If there are unapplied opportunities, show only them. Otherwise, show everything (including applied).
  const displayedOpportunities = unappliedOpportunities.length > 0 ? unappliedOpportunities : (currentList || []);

  return (
    <div className="h-full overflow-y-auto bg-gray-50 dark:bg-gray-900">
      {/* Public Navigation for non-authenticated users */}
      {!isAuthenticated && <GuestHeader />}

      <div className={!isAuthenticated ? 'pt-16' : ''}>
      {/* SEO Meta Tags */}
      <SEOHead
        title="Photography Jobs & Modeling Gigs in India | ClickCollab"
        description="Find paid photography jobs, modeling gigs, and creative collaboration opportunities across India. Filter by budget, location, and experience level. Join India's #1 platform connecting photographers, models, and creative professionals for TFP shoots, paid projects, and collaborations."
        keywords={[
          // Primary Jobs Keywords - High Volume
          'photography jobs India',
          'photographer jobs near me',
          'modeling jobs India',
          'model jobs near me',
          // Paid Gigs
          'paid photography gigs',
          'paid modeling gigs',
          'freelance photography work',
          'freelance modeling work',
          // Specific Job Types
          'wedding photography jobs',
          'event photography jobs',
          'fashion photography jobs',
          'product photography jobs',
          'portrait photography jobs',
          // Intent Keywords
          'photographer wanted',
          'model wanted',
          'looking for photographer',
          'looking for model',
          'need photographer',
          'need model for photoshoot',
          // TFP & Collaborations
          'TFP shoots India',
          'TFP photography',
          'photography collaboration',
          'model collaboration',
          // Casting & Opportunities
          'model casting calls India',
          'photography casting',
          'creative jobs India',
          'creative opportunities',
          // Freelance
          'freelance photographer India',
          'freelance model India',
          'photographer gigs',
          'model gigs',
        ]}
        url={typeof window !== 'undefined' ? window.location.href : ''}
      />
      {/* Hero Section */}
      <div className="bg-white dark:bg-gray-900 border-b border-gray-100 dark:border-gray-800 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-1.5 md:py-2">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-2 md:gap-3">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white tracking-tight">
                Opportunities
              </h1>
              <p className="text-xs md:text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                {isAuthenticated 
                  ? `Discover ${pagination.total} creative collaborations waiting for you`
                  : `Browse ${pagination.total} creative opportunities`}
              </p>
            </div>
            
            <div className="flex items-center gap-3">
              
              {/* View Mode Toggle */}
              {isAuthenticated && (
                <div className="bg-gray-100 dark:bg-gray-800 p-1 rounded-xl flex items-center">
                  <button
                    onClick={() => setViewMode('recommended')}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all flex items-center gap-1.5 ${
                      viewMode === 'recommended'
                        ? 'bg-white dark:bg-gray-700 text-black dark:text-white shadow-sm'
                        : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                    }`}
                  >
                    <SparklesIcon className="h-4 w-4" />
                    <span className="hidden md:inline">Recommended</span>
                  </button>
                  <button
                    onClick={() => setViewMode('all')}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all flex items-center gap-1.5 ${
                      viewMode === 'all'
                        ? 'bg-white dark:bg-gray-700 text-black dark:text-white shadow-sm'
                        : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                    }`}
                  >
                    <ListBulletIcon className="h-4 w-4" />
                    All
                  </button>
                </div>
              )}

              <button
                onClick={() => setShowMobileFilters(true)}
                className="p-2.5 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-black dark:hover:text-white rounded-xl border border-gray-200 dark:border-gray-700 flex items-center gap-2 transition-all"
              >
                <FunnelIcon className="h-5 w-5" />
                <span className="hidden md:inline font-medium">Filters</span>
              </button>

              {isAuthenticated && (
                <div className="flex items-center gap-3">
                  <Link to="/opportunities/my" title="My Opportunities">
                    <button className="p-2.5 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-black dark:hover:text-white rounded-xl border border-gray-200 dark:border-gray-700 transition-all">
                      <BriefcaseIcon className="h-5 w-5" />
                    </button>
                  </Link>
                  <Link to="/opportunities/post" className="shrink-0">
                    <Button variant="primary" className="h-10 px-5 flex items-center gap-2 shadow-lg shadow-black/20 hover:shadow-xl hover:shadow-black/30 transition-all whitespace-nowrap">
                      <PlusIcon className="h-4 w-4" />
                      Post New
                    </Button>
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="w-full mx-auto px-4 sm:px-6 lg:px-8 py-4 md:py-8">
        <div className="flex flex-col md:flex-row gap-8">
          
          {/* Filter Drawer (Mobile & Desktop) */}
          <AnimatePresence>
            {showMobileFilters && (
              <>
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 0.5 }}
                  exit={{ opacity: 0 }}
                  onClick={() => setShowMobileFilters(false)}
                  className="fixed inset-0 bg-black z-[60]"
                />
                <motion.div
                  initial={{ x: '100%' }}
                  animate={{ x: 0 }}
                  exit={{ x: '100%' }}
                  className="fixed inset-y-0 right-0 w-full max-w-sm bg-white dark:bg-gray-900 shadow-2xl z-[70] p-6 overflow-y-auto"
                >
                  <div className="flex items-center justify-between mb-8">
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white">Filters</h2>
                    <button onClick={() => setShowMobileFilters(false)} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors">
                      <XMarkIcon className="h-6 w-6 text-gray-500 dark:text-gray-400" />
                    </button>
                  </div>
                  <FilterSection
                    searchInput={searchInput}
                    setSearchInput={setSearchInput}
                    filters={filters}
                    onFilterChange={handleFilterChange}
                    onSearch={handleSearch}
                    onClear={clearFilters}
                  />
                </motion.div>
              </>
            )}
          </AnimatePresence>

          {/* Main Content */}
          <div className="flex-1">
            {loading && displayedOpportunities.length === 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {[...Array(8)].map((_, i) => (
                  <div key={i} className="bg-white dark:bg-gray-800 rounded-2xl h-80 animate-pulse border border-gray-100 dark:border-gray-700" />
                ))}
              </div>
            ) : displayedOpportunities.length > 0 ? (
              <>
                <div className={`grid gap-4 md:gap-6 ${
                  !isAuthenticated 
                    ? 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4' 
                    : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3'
                }`}>
                  {displayedOpportunities.map((opportunity) => (
                    <div key={opportunity.id} className="h-full">
                      <OpportunityCard 
                        opportunity={opportunity} 
                        userApplication={Array.isArray(myApplications) ? myApplications.find(app => app.opportunity_id === opportunity.id) : undefined}
                      />
                    </div>
                  ))}
                </div>
                
                <div ref={sentinelRef} className="h-10 w-full" />
                
                {/* Loading Indicator */}
                {loading && displayedOpportunities.length > 0 && (
                  <div className="flex justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-24 bg-white dark:bg-gray-800 rounded-2xl border border-dashed border-gray-200 dark:border-gray-700">
                <div className="w-20 h-20 bg-gray-50 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-6">
                  <MagnifyingGlassIcon className="h-10 w-10 text-gray-300 dark:text-gray-500" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">No opportunities found</h3>
                <p className="text-gray-500 dark:text-gray-400 mb-8 max-w-sm mx-auto leading-relaxed">
                  We couldn't find any opportunities matching your criteria. Try adjusting your filters or search terms.
                </p>
                <button
                  onClick={clearFilters}
                  className="text-black dark:text-white font-bold hover:underline underline-offset-4"
                >
                  Clear all filters
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
      </div>
    </div>
  );
};

export default OpportunitiesPage;
